import React from 'react';
import {
  GraduationCap, BookOpen, School, Award, Trophy, Users,
  MapPin, Calendar, Download, Sparkles, ArrowRight
} from 'lucide-react';
import { Section, SectionHeader } from '../components/ui/Section';
import { Card } from '../components/ui/Card';
import { Badge } from '../components/ui/Badge';
import { Button } from '../components/ui/Button';
import { Reveal, RevealGroup, RevealItem } from '../components/ui/Reveal';

const educationHistory = [
  {
    level: 'Bachelor of Engineering',
    degree: 'Computer Science & Engineering (AI & ML)',
    institution: 'Canara Engineering College',
    location: 'Mangalore, India',
    duration: '2021 - 2025',
    score: { value: '8.5', unit: '/ 10', label: 'CGPA' },
    icon: GraduationCap,
    description:
      'Four-year engineering degree specialising in Artificial Intelligence and Machine Learning, pairing core computer science with applied ML, data engineering and software design.',
    focus: ['Machine Learning', 'Deep Learning', 'Data Structures & Algorithms', 'DBMS', 'Operating Systems', 'Computer Networks', 'Computer Vision', 'NLP'],
    highlights: [
      'Ideathon competition winner for an innovative education-system concept',
      'Founding member and lead developer of the college alumni portal (Django, MySQL)',
      'Shipped ML projects including music genre classification and stroke prediction with 94–96% accuracy',
    ],
  },
  {
    level: 'Pre-University',
    degree: 'Higher Secondary Education (12th Grade)',
    institution: 'S.V.S PU College',
    location: 'Bantwal, India',
    duration: '2019 - 2021',
    score: { value: '83.5', unit: '%', label: 'Board exams' },
    icon: BookOpen,
    description:
      'Science stream with Physics, Chemistry and Mathematics, building the quantitative foundation for engineering.',
    focus: ['Physics', 'Chemistry', 'Mathematics'],
    highlights: [],
  },
  {
    level: 'Secondary School',
    degree: 'Secondary School Certificate (10th Grade)',
    institution: 'Govt. High School',
    location: 'Bantwal, India',
    duration: '2018 - 2019',
    score: { value: '87.2', unit: '%', label: 'SSLC' },
    icon: School,
    description: 'Completed secondary schooling with distinction, excelling in Mathematics and Science.',
    focus: ['Mathematics', 'Science'],
    highlights: [],
  },
];

const certifications = [
  { name: 'Python Programming', kind: 'Certification' },
  { name: 'Full Stack Web Development', kind: 'Certification' },
  { name: 'Machine Learning & AI', kind: 'Certification' },
  { name: 'Internet of Things', kind: 'Workshop' },
  { name: 'Robotic Process Automation', kind: 'Certification' },
];

const beyondClassroom = [
  { icon: Trophy, title: 'Ideathon Winner', text: 'Recognised for an innovative education-system idea.' },
  { icon: Users, title: 'Alumni Portal Lead', text: 'Founded and led the build of the college alumni network.' },
  { icon: Sparkles, title: 'Continuous Learning', text: 'Workshops, hackathons and hands-on projects beyond the syllabus.' },
];

export default function Education() {
  return (
    <Section id="education">
      <SectionHeader
        title="Education"
        subtitle="Academic foundation in computer science and AI, backed by consistent results at every stage."
        action={
          <Button asChild variant="secondary" size="md">
            <a href="/resume/Rakshan_Shetty_Resume.pdf" download>
              <Download size={14} className="mr-1.5" />
              Download Resume
            </a>
          </Button>
        }
      />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 items-start">
        {/* Timeline */}
        <RevealGroup as="ol" className="lg:col-span-2 relative space-y-6">
          <span
            aria-hidden="true"
            className="absolute left-[15px] top-6 bottom-6 w-px bg-[var(--bg-recessed)]"
          />
          {educationHistory.map((edu) => {
            const Icon = edu.icon;
            return (
              <RevealItem as="li" key={edu.degree} className="relative pl-12">
                <span className="absolute left-0 top-5 w-8 h-8 rounded-full bg-[var(--bg-elevated)] shadow-[var(--shadow-sm)] flex items-center justify-center text-[var(--fg-primary)]">
                  <Icon size={15} />
                </span>

                <Card className="p-5 md:p-6 space-y-5">
                  <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4">
                    <div className="min-w-0">
                      <p className="t-eyebrow text-[var(--fg-secondary)] mb-1">{edu.level}</p>
                      <h3 className="t-h4 text-[var(--fg-primary)]">{edu.degree}</h3>
                      <p className="mt-1 text-sm font-medium text-[var(--fg-primary)]">{edu.institution}</p>
                      <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-[var(--fg-secondary)]">
                        <span className="flex items-center gap-1">
                          <Calendar size={12} />
                          {edu.duration}
                        </span>
                        <span className="flex items-center gap-1">
                          <MapPin size={12} />
                          {edu.location}
                        </span>
                      </div>
                    </div>

                    <div className="shrink-0 sm:text-right px-4 py-3 rounded-md bg-[var(--bg-recessed)] sm:min-w-[120px]">
                      <p className="t-h4 text-[var(--fg-primary)] font-semibold leading-none">
                        {edu.score.value}
                        <span className="text-sm font-normal text-[var(--fg-secondary)] ml-0.5">{edu.score.unit}</span>
                      </p>
                      <p className="mt-1.5 text-[10px] uppercase tracking-wider font-mono text-[var(--fg-secondary)]">
                        {edu.score.label}
                      </p>
                    </div>
                  </div>

                  <p className="text-sm text-[var(--fg-secondary)] leading-relaxed">{edu.description}</p>

                  <div>
                    <h4 className="t-eyebrow text-[var(--fg-primary)] mb-2">Focus areas</h4>
                    <div className="flex flex-wrap gap-1.5">
                      {edu.focus.map((item) => (
                        <Badge key={item}>{item}</Badge>
                      ))}
                    </div>
                  </div>

                  {edu.highlights.length > 0 && (
                    <div>
                      <h4 className="t-eyebrow text-[var(--fg-primary)] mb-2">Highlights</h4>
                      <ul className="space-y-1.5">
                        {edu.highlights.map((item) => (
                          <li key={item} className="flex items-start gap-2 text-sm text-[var(--fg-secondary)]">
                            <ArrowRight size={14} className="mt-1 shrink-0 text-[var(--fg-muted)]" />
                            <span>{item}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </Card>
              </RevealItem>
            );
          })}
        </RevealGroup>

        {/* Sidebar */}
        <Reveal className="space-y-4 lg:sticky lg:top-24" delay={0.1}>
          <Card className="p-5">
            <h3 className="t-eyebrow text-[var(--fg-primary)] mb-3">Academic snapshot</h3>
            <div className="grid grid-cols-3 gap-2">
              {educationHistory.map((edu) => (
                <div key={edu.degree} className="p-3 rounded-md bg-[var(--bg-recessed)] text-center">
                  <p className="t-h6 text-[var(--fg-primary)] font-semibold mb-0.5">
                    {edu.score.value}
                    <span className="text-[10px] font-normal text-[var(--fg-secondary)]">{edu.score.unit}</span>
                  </p>
                  <p className="text-[10px] text-[var(--fg-secondary)] font-mono">{edu.score.label}</p>
                </div>
              ))}
            </div>
          </Card>

          <Card className="p-5">
            <h3 className="t-eyebrow text-[var(--fg-primary)] mb-3 flex items-center gap-1.5">
              <Award size={14} className="text-[var(--fg-secondary)]" />
              Certifications
            </h3>
            <ul className="divide-y divide-[var(--bg-recessed)]">
              {certifications.map((cert) => (
                <li key={cert.name} className="flex items-center justify-between gap-3 py-2 first:pt-0 last:pb-0">
                  <span className="text-sm text-[var(--fg-primary)]">{cert.name}</span>
                  <Badge className="shrink-0">{cert.kind}</Badge>
                </li>
              ))}
            </ul>
          </Card>

          <Card className="p-5">
            <h3 className="t-eyebrow text-[var(--fg-primary)] mb-3">Beyond the classroom</h3>
            <ul className="space-y-3">
              {beyondClassroom.map(({ icon: Icon, title, text }) => (
                <li key={title} className="flex items-start gap-3">
                  <span className="mt-0.5 w-7 h-7 shrink-0 rounded-md bg-[var(--bg-recessed)] flex items-center justify-center text-[var(--fg-secondary)]">
                    <Icon size={14} />
                  </span>
                  <div>
                    <p className="text-sm font-medium text-[var(--fg-primary)]">{title}</p>
                    <p className="text-xs text-[var(--fg-secondary)] leading-relaxed">{text}</p>
                  </div>
                </li>
              ))}
            </ul>
          </Card>
        </Reveal>
      </div>
    </Section>
  );
}
